const DEFAULT_RESERVED_PARAMS = ["q", "sortBy", "sortDir", "id"];

function isActiveFilterValue(value) {
  return value !== null && value !== undefined && value !== "";
}

export function hasUrlFilterParams(searchParams, config = {}) {
  const reserved = new Set(config.reservedParams ?? DEFAULT_RESERVED_PARAMS);
  const paramMap = config.paramMap ?? {};

  for (const [filterKey, paramName] of Object.entries(paramMap)) {
    if (reserved.has(paramName)) {
      continue;
    }
    if (searchParams.has(paramName) && searchParams.get(paramName)) {
      return true;
    }
    void filterKey;
  }

  return Array.from(searchParams.entries()).some(([key, value]) => {
    if (reserved.has(key)) {
      return false;
    }
    return Boolean(value);
  });
}

export function parseUrlSearchParamsToFilters(searchParams, config = {}) {
  const reserved = new Set(config.reservedParams ?? DEFAULT_RESERVED_PARAMS);
  const paramMap = config.paramMap ?? {};
  const reverseMap = Object.fromEntries(
    Object.entries(paramMap).map(([filterKey, paramName]) => [paramName, filterKey])
  );

  const filters = {};

  searchParams.forEach((value, paramName) => {
    if (reserved.has(paramName) || !value) {
      return;
    }

    const filterKey = reverseMap[paramName] ?? paramName;
    filters[filterKey] = value;
  });

  return filters;
}

export function buildUrlSearchParamsFromFilters(
  filters,
  config = {},
  existingParams
) {
  const reserved = new Set(config.reservedParams ?? DEFAULT_RESERVED_PARAMS);
  const paramMap = config.paramMap ?? {};
  const params = new URLSearchParams(existingParams?.toString() ?? "");

  const managedFilterKeys = new Set(Object.keys(paramMap));
  const managedParamNames = new Set(Object.values(paramMap));

  Object.keys(paramMap).forEach((filterKey) => {
    params.delete(paramMap[filterKey]);
  });

  Object.keys(filters).forEach((filterKey) => {
    const paramName = paramMap[filterKey] ?? filterKey;
    params.delete(paramName);
  });

  Object.entries(filters).forEach(([filterKey, value]) => {
    if (!isActiveFilterValue(value)) {
      return;
    }

    const paramName = paramMap[filterKey] ?? filterKey;
    params.set(paramName, String(value));
  });

  if (existingParams) {
    existingParams.forEach((value, key) => {
      if (reserved.has(key)) {
        params.set(key, value);
      } else if (!managedParamNames.has(key) && !managedFilterKeys.has(key)) {
        params.set(key, value);
      }
    });
  }

  return params;
}

export function resolveFiltersWithPriority({
  urlFilters,
  persistedFilters,
  defaultFilters = {},
}) {
  const urlHasFilters = Object.values(urlFilters).some(isActiveFilterValue);
  if (urlHasFilters) {
    return { filters: { ...urlFilters }, source: "url" };
  }

  const persistedHasFilters = Object.values(persistedFilters).some(isActiveFilterValue);
  if (persistedHasFilters) {
    return { filters: { ...persistedFilters }, source: "persisted" };
  }

  return { filters: { ...defaultFilters }, source: "default" };
}

export function moduleFiltersAreEqual(a, b) {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);

  for (const key of keys) {
    const left = a[key] ?? null;
    const right = b[key] ?? null;
    if (String(left ?? "") !== String(right ?? "")) {
      return false;
    }
  }

  return true;
}
